import { FC, useState } from 'react';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button as AntBtn } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Button, DatePicker, Label, Typography } from 'ui';

import { Day } from 'types/promoters/promoterTypes';

import s from './EventSchedule.module.scss';

interface DayItemProps {
  day: Day;
  onUpdate: (updatedDay: Day) => void;
  onDelete: () => void;
  onEdit: (name: string) => void;
}

const DayItem: FC<DayItemProps> = ({ day, onUpdate, onDelete, onEdit }) => {
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [time, setTime] = useState<Dayjs>();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAddTimeSlot = () => {
    if (!time) return;

    const formattedTime = time.format('HH:mm');
    const updatedSlots = [...day.timeslots];

    if (editingIndex !== null) {
      updatedSlots[editingIndex] = { time: formattedTime };
    } else {
      updatedSlots.push({ time: formattedTime });
    }

    onUpdate({
      ...day,
      timeslots: updatedSlots,
    });

    // Reset form
    setTime(undefined);
    setEditingIndex(null);
    setShowTimeForm(false);
  };

  const handleEditTimeSlot = (index: number) => {
    setEditingIndex(index);
    setTime(dayjs(day.timeslots[index].time, 'HH:mm'));
    setShowTimeForm(true);
  };

  const handleDeleteTimeSlot = (index: number) => {
    const updatedSlots = day.timeslots.filter((_, i) => i !== index);
    onUpdate({
      ...day,
      timeslots: updatedSlots,
    });
  };

  return (
    <div className={s.dayItemWrapper}>
      <div className={s.dayItem}>
        <div>
          <Typography type="main" weight={700}>
            {day.dayOfWeek.charAt(0).toUpperCase() + day.dayOfWeek.slice(1).toLowerCase()}
          </Typography>
          <div className={s.timeSlots}>
            {day.timeslots.map((slot, idx) => (
              <div key={idx} className={s.timeItem}>
                <Typography type="small" weight={800}>
                  {slot.time || ''}
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <AntBtn
                    type="link"
                    style={{ fontWeight: 700, color: '#187DFD', width: 80 }}
                    onClick={() => handleEditTimeSlot(idx)}>
                    Edit
                  </AntBtn>
                  <AntBtn
                    type="link"
                    style={{ fontWeight: 700, color: '#FF4B53', width: 80 }}
                    onClick={() => handleDeleteTimeSlot(idx)}>
                    Delete
                  </AntBtn>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={s.actionButtonContainter}>
          <Button size="small" className={s.actionButton} onClick={() => setShowTimeForm(true)}>
            TIMESLOTS
          </Button>
          <Button size="small" className={s.actionButton} onClick={() => onEdit(day.dayOfWeek)}>
            <EditOutlined />
            EDIT
          </Button>
          <Button size="small" className={s.deleteButton} onClick={onDelete}>
            <DeleteOutlined />
            DELETE
          </Button>
        </div>
      </div>
      {showTimeForm && (
        <div className={s.timeForm}>
          <Typography type="h6">
            Times of day for {day?.dayOfWeek} when event will happen
          </Typography>
          <Label>Input time when show happens (ex: 13:00)</Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <DatePicker.TimePicker value={time} onChange={setTime} format="HH:mm" />
            <Button disabled={!time} type="primary" onClick={handleAddTimeSlot}>
              {editingIndex !== null ? 'UPDATE TIME' : 'ADD TIME'}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setShowTimeForm(false);
                setEditingIndex(null);
                setTime(undefined);
              }}>
              CANCEL
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayItem;
